import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { SERVICES, buildQuoteWhatsAppMessage, waLink } from "@/config/brand";

export const HeroQuoteForm = () => {
    const [values, setValues] = useState({
        full_name: "", phone: "", email: "", service: "House Removals",
        collection_postcode: "", delivery_postcode: "", moving_date: "",
    });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e?.target ? e.target.value : e }));

    const submit = (e) => {
        e.preventDefault();
        if (!values.full_name || values.full_name.trim().length < 2) return toast.error("Please enter your name.");
        if (!values.phone) return toast.error("Please provide a phone number.");
        if (!values.email) return toast.error("Please provide an email address.");
        setLoading(true);
        try {
            const message = buildQuoteWhatsAppMessage(values);
            toast.success("Opening WhatsApp — press Send to complete your enquiry.");
            window.open(waLink(message), "_blank", "noopener,noreferrer");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            data-testid={HOME.heroForm}
            onSubmit={submit}
            className="rounded-2xl bg-white text-[#17252D] p-5 sm:p-6 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
        >
            <div className="font-heading text-lg font-semibold">Get a free quote in 60 seconds</div>
            <p className="mt-1 text-sm text-[#17252D]/70">WhatsApp will open with your details pre-filled — just press Send.</p>

            <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="col-span-2">
                    <Label>Full name</Label>
                    <Input required data-testid="hero-input-name" value={values.full_name} onChange={set("full_name")} placeholder="Your name" />
                </div>
                <div>
                    <Label>Phone</Label>
                    <Input required data-testid="hero-input-phone" value={values.phone} onChange={set("phone")} placeholder="07…" />
                </div>
                <div>
                    <Label>Email</Label>
                    <Input required type="email" data-testid="hero-input-email" value={values.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div className="col-span-2">
                    <Label>Service</Label>
                    <Select value={values.service} onValueChange={(v) => set("service")(v)}>
                        <SelectTrigger data-testid="hero-input-service"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {SERVICES.map((s) => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>From postcode</Label>
                    <Input data-testid="hero-input-from" value={values.collection_postcode} onChange={set("collection_postcode")} placeholder="e.g. SW1A 1AA" />
                </div>
                <div>
                    <Label>To postcode</Label>
                    <Input data-testid="hero-input-to" value={values.delivery_postcode} onChange={set("delivery_postcode")} placeholder="e.g. E1 6AN" />
                </div>
                <div className="col-span-2">
                    <Label>Preferred date</Label>
                    <Input type="date" data-testid="hero-input-date" value={values.moving_date} onChange={set("moving_date")} />
                </div>
            </div>
            <Button
                type="submit"
                disabled={loading}
                data-testid={HOME.heroFormSubmit}
                className="mt-4 w-full bg-[#25D366] hover:bg-[#20b955] text-white h-11 font-semibold"
            >
                <MessageCircle className="h-4 w-4 mr-2" /> {loading ? "Opening WhatsApp…" : "Get a Quote on WhatsApp"}
            </Button>
            <p className="mt-2 text-xs text-[#17252D]/60">
                WhatsApp opens pre-filled with your details — you just press Send.
            </p>
        </form>
    );
};
